import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, getApiErrorMessage } from '../../lib/api'
import { useToast } from '../../ui/ToastProvider'

const EMPTY_PRODUCT = {
  productCode: '',
  name: '',
  description: '',
  paidPointAmount: 0,
  bonusPointAmount: 0,
  listPrice: 0,
  currency: 'KRW',
  paidPointExpireDays: '',
  displayOrder: 100,
  active: false,
}

const EMPTY_CHANNEL = {
  id: null,
  provider: 'google_play',
  salesChannel: 'google_play',
  externalProductId: '',
  storePrice: '',
  currency: 'KRW',
  displayName: '',
  active: false,
}

const providerLabel = (provider) => {
  switch (String(provider || '').toLowerCase()) {
    case 'apple_iap': return 'App Store'
    case 'google_play': return 'Google Play'
    default: return provider || '-'
  }
}

const money = (amount, currency = 'KRW') => {
  const formatted = Number(amount || 0).toLocaleString('ko-KR')
  return String(currency || 'KRW').toUpperCase() === 'KRW' ? `${formatted}원` : `${formatted} ${currency}`
}

const dateTime = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('ko-KR')
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-lg border bg-white p-5 shadow-xl" onClick={(event) => event.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <button type="button" className="rounded border px-2 py-1 text-sm text-slate-600 hover:bg-slate-50" onClick={onClose}>닫기</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {children}
    </label>
  )
}

const inputClass = 'w-full rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-navy'

export default function PointCommerceAdminPanel() {
  const { show } = useToast()
  const [products, setProducts] = useState([])
  const [purchases, setPurchases] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [productDraft, setProductDraft] = useState(null)
  const [channelTarget, setChannelTarget] = useState(null)
  const [channelDraft, setChannelDraft] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [productsResponse, purchasesResponse] = await Promise.all([
        api.get('/billing/points/admin/products'),
        api.get('/billing/points/admin/purchases', { params: { page: 0, size: 30 } }),
      ])
      setProducts(Array.isArray(productsResponse?.data?.data) ? productsResponse.data.data : [])
      setPurchases(Array.isArray(purchasesResponse?.data?.data?.content) ? purchasesResponse.data.data.content : [])
    } catch (e) {
      setError(getApiErrorMessage(e, '포인트 상품과 구매 내역을 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const openNewProduct = () => setProductDraft({ ...EMPTY_PRODUCT })
  const openProduct = (product) => setProductDraft({
    id: product.id,
    productCode: product.productCode || '',
    name: product.name || '',
    description: product.description || '',
    paidPointAmount: product.paidPointAmount ?? 0,
    bonusPointAmount: product.bonusPointAmount ?? 0,
    listPrice: product.listPrice ?? 0,
    currency: product.currency || 'KRW',
    paidPointExpireDays: product.paidPointExpireDays ?? '',
    displayOrder: product.displayOrder ?? 100,
    active: product.active === true,
  })

  const saveProduct = async () => {
    if (!String(productDraft?.productCode || '').trim() || !String(productDraft?.name || '').trim()) {
      show('상품 코드와 상품명을 입력해 주세요.', 'error')
      return
    }
    setSaving(true)
    try {
      const payload = {
        ...productDraft,
        paidPointAmount: Number(productDraft.paidPointAmount || 0),
        bonusPointAmount: Number(productDraft.bonusPointAmount || 0),
        listPrice: Number(productDraft.listPrice || 0),
        paidPointExpireDays: productDraft.paidPointExpireDays === '' ? null : Number(productDraft.paidPointExpireDays),
        displayOrder: Number(productDraft.displayOrder || 100),
      }
      if (productDraft.id) await api.put(`/billing/points/admin/products/${productDraft.id}`, payload)
      else await api.post('/billing/points/admin/products', payload)
      show('포인트 상품을 저장했습니다.', 'success')
      setProductDraft(null)
      await load()
    } catch (e) {
      show(getApiErrorMessage(e, '포인트 상품을 저장하지 못했습니다.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const openChannel = (product, channel = null) => {
    setChannelTarget(product)
    setChannelDraft(channel ? {
      id: channel.id,
      provider: channel.provider || 'google_play',
      salesChannel: channel.salesChannel || channel.provider || 'google_play',
      externalProductId: channel.externalProductId || '',
      storePrice: channel.storePrice ?? '',
      currency: channel.currency || product.currency || 'KRW',
      displayName: channel.displayName || '',
      active: channel.active === true,
    } : { ...EMPTY_CHANNEL, currency: product.currency || 'KRW' })
  }

  const updateChannelProvider = (provider) => {
    setChannelDraft((current) => ({ ...current, provider, salesChannel: provider }))
  }

  const saveChannel = async () => {
    if (!String(channelDraft?.provider || '').trim() || !String(channelDraft?.externalProductId || '').trim()) {
      show('결제사와 외부 상품 ID를 입력해 주세요.', 'error')
      return
    }
    setSaving(true)
    try {
      await api.put(`/billing/points/admin/products/${channelTarget.id}/channels`, {
        ...channelDraft,
        storePrice: channelDraft.storePrice === '' ? null : Number(channelDraft.storePrice),
      })
      show('판매 채널을 저장했습니다.', 'success')
      setChannelTarget(null)
      setChannelDraft(null)
      await load()
    } catch (e) {
      show(getApiErrorMessage(e, '판매 채널을 저장하지 못했습니다.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const retryFinalization = async (purchase) => {
    try {
      await api.post(`/billing/points/admin/purchases/${purchase.id}/retry-finalization`)
      show('스토어 소비 처리를 다시 확인했습니다.', 'success')
      await load()
    } catch (e) {
      show(getApiErrorMessage(e, '스토어 소비 처리를 재시도하지 못했습니다.'), 'error')
    }
  }

  const refundPurchase = async (purchase) => {
    const storePurchase = ['apple_iap', 'google_play'].includes(String(purchase.provider || '').toLowerCase())
    const message = storePurchase
      ? '스토어에서 전액 환불이 완료된 구매만 서버 포인트를 회수할 수 있습니다. 계속할까요?'
      : 'PG 전액 환불과 포인트 회수를 진행할까요?'
    if (!window.confirm(message)) return
    try {
      await api.post(`/billing/points/admin/purchases/${purchase.id}/refund`, {
        reason: '운영자 전액 환불 처리',
        providerAlreadyRefunded: storePurchase,
      })
      show('환불 포인트 회수 처리를 완료했습니다.', 'success')
      await load()
    } catch (e) {
      show(getApiErrorMessage(e, '환불 포인트를 회수하지 못했습니다.'), 'error')
    }
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">포인트 상품·결제</h2>
          <p className="mt-1 text-xs text-slate-500">상품과 판매 채널을 모두 활성화하면 해당 채널에 자동 노출됩니다.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link className="rounded border px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" to="/subscriptions/config">기능별 차감 설정</Link>
          <button type="button" className="rounded bg-brand-navy px-3 py-2 text-sm font-medium text-white" onClick={openNewProduct}>상품 추가</button>
        </div>
      </div>

      {error && <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div>}
      {loading ? (
        <div className="rounded border bg-white px-4 py-8 text-center text-sm text-slate-500">불러오는 중입니다.</div>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">상품</th>
                <th className="px-3 py-2 text-right">포인트</th>
                <th className="px-3 py-2 text-right">기준가</th>
                <th className="px-3 py-2 text-left">판매 채널</th>
                <th className="px-3 py-2 text-center">상태</th>
                <th className="px-3 py-2 text-right">관리</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id} className="border-t align-top">
                  <td className="px-3 py-2"><div className="font-medium text-slate-900">{product.name}</div><div className="text-xs text-slate-500">{product.productCode}</div></td>
                  <td className="px-3 py-2 text-right"><div>{Number(product.totalPointAmount || 0).toLocaleString()}P</div><div className="text-xs text-slate-500">유료 {Number(product.paidPointAmount || 0).toLocaleString()} · 보너스 {Number(product.bonusPointAmount || 0).toLocaleString()}</div></td>
                  <td className="px-3 py-2 text-right">{money(product.listPrice, product.currency)}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(product.channels || []).map((channel) => (
                        <button key={channel.id} type="button" className={`rounded border px-2 py-1 text-xs ${channel.active ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'text-slate-500'}`} onClick={() => openChannel(product, channel)}>
                          {providerLabel(channel.provider)} · {channel.active ? '판매' : '중지'}
                        </button>
                      ))}
                      <button type="button" className="rounded border border-dashed px-2 py-1 text-xs text-slate-600 hover:bg-slate-50" onClick={() => openChannel(product)}>채널 추가</button>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center"><span className={`rounded px-2 py-1 text-xs ${product.active ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>{product.active ? '판매 허용' : '비활성'}</span></td>
                  <td className="px-3 py-2 text-right"><button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => openProduct(product)}>수정</button></td>
                </tr>
              ))}
              {products.length === 0 && <tr><td className="px-4 py-8 text-center text-slate-500" colSpan="6">등록된 포인트 상품이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-900">최근 포인트 구매</h3>
        <div className="overflow-x-auto rounded border bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600"><tr><th className="px-3 py-2 text-left">결제일</th><th className="px-3 py-2 text-left">회원</th><th className="px-3 py-2 text-left">상품·채널</th><th className="px-3 py-2 text-right">결제·포인트</th><th className="px-3 py-2 text-left">상태</th><th className="px-3 py-2 text-right">관리</th></tr></thead>
            <tbody>
              {purchases.map((purchase) => (
                <tr key={purchase.id} className="border-t">
                  <td className="px-3 py-2 whitespace-nowrap">{dateTime(purchase.paidAt || purchase.createdAt)}</td>
                  <td className="px-3 py-2">{purchase.userEmail || '-'}</td>
                  <td className="px-3 py-2"><div>{purchase.productName}</div><div className="text-xs text-slate-500">{providerLabel(purchase.provider)}</div></td>
                  <td className="px-3 py-2 text-right"><div>{money(purchase.amount, purchase.currency)}</div><div className="text-xs text-slate-500">{Number(purchase.totalPointAmount || 0).toLocaleString()}P</div></td>
                  <td className="px-3 py-2"><div>{purchase.statusLabel || '확인 필요'}</div>{Number(purchase.unrecoveredPointAmount || 0) > 0 && <div className="text-xs font-medium text-rose-700">미회수 {Number(purchase.unrecoveredPointAmount).toLocaleString()}P</div>}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {purchase.finalizationPending && <button type="button" className="mr-1 rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => retryFinalization(purchase)}>소비 재시도</button>}
                    {['FULFILLED', 'FULFILLED_PENDING_FINALIZATION'].includes(purchase.status) && <button type="button" className="rounded border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50" onClick={() => refundPurchase(purchase)}>전액 환불</button>}
                  </td>
                </tr>
              ))}
              {purchases.length === 0 && <tr><td className="px-4 py-8 text-center text-slate-500" colSpan="6">포인트 구매 내역이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {productDraft && (
        <Modal title={productDraft.id ? '포인트 상품 수정' : '포인트 상품 추가'} onClose={() => setProductDraft(null)}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="상품 코드"><input className={inputClass} value={productDraft.productCode} disabled={Boolean(productDraft.id)} onChange={(e) => setProductDraft({ ...productDraft, productCode: e.target.value.toUpperCase() })} /></Field>
            <Field label="상품명"><input className={inputClass} value={productDraft.name} onChange={(e) => setProductDraft({ ...productDraft, name: e.target.value })} /></Field>
            <Field label="유료 포인트"><input className={inputClass} type="number" min="0" value={productDraft.paidPointAmount} onChange={(e) => setProductDraft({ ...productDraft, paidPointAmount: e.target.value })} /></Field>
            <Field label="보너스 포인트"><input className={inputClass} type="number" min="0" value={productDraft.bonusPointAmount} onChange={(e) => setProductDraft({ ...productDraft, bonusPointAmount: e.target.value })} /></Field>
            <Field label="기준 가격"><input className={inputClass} type="number" min="0" value={productDraft.listPrice} onChange={(e) => setProductDraft({ ...productDraft, listPrice: e.target.value })} /></Field>
            <Field label="통화"><input className={inputClass} value={productDraft.currency} onChange={(e) => setProductDraft({ ...productDraft, currency: e.target.value.toUpperCase() })} /></Field>
            <Field label="유효기간(일)"><input className={inputClass} type="number" min="1" value={productDraft.paidPointExpireDays} placeholder="미입력 시 무기한" onChange={(e) => setProductDraft({ ...productDraft, paidPointExpireDays: e.target.value })} /></Field>
            <Field label="노출 순서"><input className={inputClass} type="number" value={productDraft.displayOrder} onChange={(e) => setProductDraft({ ...productDraft, displayOrder: e.target.value })} /></Field>
            <Field label="설명"><input className={inputClass} value={productDraft.description} onChange={(e) => setProductDraft({ ...productDraft, description: e.target.value })} /></Field>
            <label className="flex items-center gap-2 self-end rounded border px-3 py-2 text-sm"><input type="checkbox" checked={productDraft.active} onChange={(e) => setProductDraft({ ...productDraft, active: e.target.checked })} />판매 허용</label>
          </div>
          <div className="mt-4 flex justify-end gap-2"><button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setProductDraft(null)}>취소</button><button type="button" className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50" disabled={saving} onClick={saveProduct}>{saving ? '저장 중' : '저장'}</button></div>
        </Modal>
      )}

      {channelDraft && channelTarget && (
        <Modal title={`${channelTarget.name} 판매 채널`} onClose={() => { setChannelTarget(null); setChannelDraft(null) }}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="결제사"><input className={inputClass} list="point-provider-options" value={channelDraft.provider} onChange={(e) => updateChannelProvider(e.target.value)} /><datalist id="point-provider-options"><option value="google_play" /><option value="apple_iap" /></datalist></Field>
            <Field label="외부 상품 ID"><input className={inputClass} value={channelDraft.externalProductId} onChange={(e) => setChannelDraft({ ...channelDraft, externalProductId: e.target.value })} /></Field>
            <Field label="채널 판매가"><input className={inputClass} type="number" min="0" value={channelDraft.storePrice} placeholder="미입력 시 기준가" onChange={(e) => setChannelDraft({ ...channelDraft, storePrice: e.target.value })} /></Field>
            <Field label="통화"><input className={inputClass} value={channelDraft.currency} onChange={(e) => setChannelDraft({ ...channelDraft, currency: e.target.value.toUpperCase() })} /></Field>
            <Field label="표시 상품명"><input className={inputClass} value={channelDraft.displayName} onChange={(e) => setChannelDraft({ ...channelDraft, displayName: e.target.value })} /></Field>
            <label className="flex items-center gap-2 self-end rounded border px-3 py-2 text-sm"><input type="checkbox" checked={channelDraft.active} onChange={(e) => setChannelDraft({ ...channelDraft, active: e.target.checked })} />채널 판매 활성화</label>
          </div>
          <div className="mt-4 flex justify-end gap-2"><button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => { setChannelTarget(null); setChannelDraft(null) }}>취소</button><button type="button" className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50" disabled={saving} onClick={saveChannel}>{saving ? '저장 중' : '저장'}</button></div>
        </Modal>
      )}
    </section>
  )
}
